<template>
	<div>
		<div
			v-if="loading"
			class="payment-skeleton"
			aria-label="Loading"
			aria-busy="true"
			aria-live="polite"
		>
			<div
				aria-hidden="true"
				role="presentation"
				class="payment-skeleton__item"
			></div>
			<div
				aria-hidden="true"
				role="presentation"
				class="payment-skeleton__item"
			></div>
			<div
				aria-hidden="true"
				role="presentation"
				class="payment-skeleton__item --half"
			></div>
			<div
				aria-hidden="true"
				role="presentation"
				class="payment-skeleton__item --half"
			></div>
		</div>

		<div v-show="!loading" class="payment-form">
			<VSInputWrapper
				class="payment-form__item"
				label="Card Number"
				:active="fieldHasData.number"
				:error-message="
					validationErrors.includes('number') &&
					'Credit card number invalid'
				"
			>
				<div
					id="braintree_field_credit_card"
					class="vs-input-wrapper__input"
					placeholder="Card Number"
				/>
			</VSInputWrapper>
			<VSInputWrapper
				class="payment-form__item"
				label="Name on card"
				:active="fieldHasData.cardholderName"
				:error-message="
					validationErrors.includes('cardholderName') &&
					'Name on card invalid'
				"
			>
				<div
					id="braintree_field_cardholder_name"
					class="vs-input-wrapper__input"
					placeholder="Name on card"
				/>
			</VSInputWrapper>

			<VSInputWrapper
				class="payment-form__item --half-desktop"
				label="Expiration Date (MM / YY)"
				:active="fieldHasData.expirationDate"
				:error-message="
					validationErrors.includes('expirationDate') &&
					'Expiration date invalid'
				"
			>
				<div
					id="braintree_field_expiration"
					class="vs-input-wrapper__input"
					placeholder="Expiration Date (MM / YY)"
				/>
			</VSInputWrapper>
			<VSInputWrapper
				class="payment-form__item --half-desktop"
				label="Security Code"
				:active="fieldHasData.cvv"
				:error-message="
					validationErrors.includes('cvv') && 'Security code invalid'
				"
			>
				<div
					id="braintree_field_cvv"
					class="vs-input-wrapper__input"
					placeholder="Security Code"
				/>
			</VSInputWrapper>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import * as braintree from 'braintree-web';
import { VSInputWrapper } from '@component/UI/controls';

export default {
	components: {
		VSInputWrapper,
	},
	props: {
		gateway: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			processing: false,
			validationErrors: [],
			validControls: [],
			clientInstance: null,
			hostedFieldsInstance: null,
			fieldHasData: {
				number: false,
				cardholderName: false,
				expirationDate: false,
				cvv: false,
			},
			loading: true,
			payPalInstance: null,
		};
	},
	computed: mapGetters('checkout', [
		'paypalOrderOptions',
		'hasBumpers',
		'enablePaypal',
	]),
	async mounted() {
		await this.createInstance();

		if (this.enablePaypal) {
			this.payPalInstance = await this.createPaypalInstance();
		}

		this.loading = false;
	},
	methods: {
		...mapMutations('checkout', ['setInitializingPaypal']),
		...mapActions('checkout', ['paypalCompleteOrder']),

		async createInstance() {
			try {
				this.clientInstance = await this.createClient();
				this.hostedFieldsInstance = await this.createHostedFields();

				this.hostedFieldsInstance.on('notEmpty', (event) => {
					this.fieldHasData[event.emittedBy] = true;
				});

				this.hostedFieldsInstance.on('empty', (event) => {
					this.fieldHasData[event.emittedBy] = false;
				});

				this.hostedFieldsInstance.on('validityChange', (event) => {
					const field = event.fields[event.emittedBy];
					// If the field is now valid
					if (field.isValid) {
						// Render the valid control feedback
						this.validControls.push(event.emittedBy);

						// Remove the validation errors for that field
						this.validationErrors = this.validationErrors.filter(
							(validationError) =>
								validationError !== event.emittedBy
						);
					} else if (field.isPotentiallyValid) {
						// Remove all validation from fields while the field is potentially valid
						this.validControls = this.validControls.filter(
							(control) => control !== event.emittedBy
						);
						this.validationErrors = this.validationErrors.filter(
							(validationError) =>
								validationError !== event.emittedBy
						);
					} else {
						// Add validation errors and remove valid control feedback
						this.validationErrors.push(event.emittedBy);
						this.validControls = this.validControls.filter(
							(control) => control !== event.emittedBy
						);
					}
				});
			} catch (error) {
				this.$ErrorHandler.reportError(
					'Failed to create Braintree instance!',
					error
				);
			}
		},

		async createPaypalInstance() {
			this.setInitializingPaypal(true);

			const el = '#paypal-button';

			return braintree.paypalCheckout
				.create({
					client: this.clientInstance,
				})
				.then((paypalCheckoutInstance) =>
					paypalCheckoutInstance.loadPayPalSDK({
						vault: true,
					})
				)
				.then((paypalCheckoutInstance) => {
					window.paypal
						.Buttons({
							fundingSource: window.paypal.FUNDING.PAYPAL,
							style: {
								label: this.hasBumpers ? 'pay' : 'paypal',
							},
							createBillingAgreement: () =>
								paypalCheckoutInstance.createPayment(
									this.paypalOrderOptions
								),

							onApprove: (data) =>
								paypalCheckoutInstance
									.tokenizePayment(data)
									.then((payload) => {
										this.paypalCompleteOrder(payload);
									}),
						})
						.render(el)
						.then(() => {
							this.setInitializingPaypal(false);
						});
				});
		},

		// eslint-disable-next-line consistent-return
		async createClient() {
			return braintree.client
				.create({
					authorization: window.braintreeClientToken || '',
				})
				.catch((err) => {
					this.$ErrorHandler.reportError(
						`Failed to create Braintree client!`,
						err
					);
				});
		},

		// eslint-disable-next-line consistent-return
		async createHostedFields() {
			const fields = {
				number: {
					selector: '#braintree_field_credit_card',
					placeholder: 'Card Number',
				},
				cardholderName: {
					selector: '#braintree_field_cardholder_name',
					placeholder: 'Name on card',
				},
				expirationDate: {
					selector: '#braintree_field_expiration',
					placeholder: 'Expiration Date (MM / YY)',
				},
				cvv: {
					selector: '#braintree_field_cvv',
					placeholder: 'Security Code',
				},
			};

			const styles = {
				input: {
					color: '#343a40',
					transition: 'border-color 150ms ease',
					'font-size': '14px',
				},
				'::placeholder': {
					color: '#6c757d',
				},
			};

			try {
				return await braintree.hostedFields.create({
					client: this.clientInstance,
					styles,
					fields,
				});
			} catch (error) {
				this.$ErrorHandler.reportError(
					'Failed to create hosted fields instance!',
					error
				);
			}
		},

		// eslint-disable-next-line consistent-return
		async getToken() {
			if (this.hostedFieldsInstance) {
				this.processing = true;

				try {
					const response = await this.hostedFieldsInstance.tokenize();
					this.validationErrors = [];
					this.processing = false;
					return {
						token: response.nonce,
						gateway: this.gateway,
						details: response.details,
					};
				} catch (error) {
					this.handleError(error);
					this.processing = false;
					throw error;
				}
			}
		},

		// eslint-disable-next-line consistent-return
		handleError(error) {
			const validationErrors = [];

			if (error.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
				error.details.invalidFieldKeys.forEach((key) =>
					validationErrors.push(key)
				);
			} else if (error.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
				validationErrors.push('number');
				validationErrors.push('expirationDate');
				validationErrors.push('cardholderName');
				validationErrors.push('cvv');
				validationErrors.push('postalCode');
			} else if (
				error.code ===
				'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED'
			) {
				validationErrors.push('cvv');
			} else if (
				error.code === 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE'
			) {
				validationErrors.push('number');
			} else {
				// 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR'
				// 'HOSTED_FIELDS_FAILED_TOKENIZATION'
				return window.location.reload();
			}

			this.validationErrors = validationErrors;
		},
	},
};
</script>

<style lang="scss" scoped>
.payment-form {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: var(--size-base);

	&__item {
		grid-column: span 2;

		&.--half-desktop {
			@media screen and (min-width: 992px) {
				grid-column: span 1;
			}
		}
	}
}

label {
	display: block;
	margin-bottom: 0.5rem;
}

.braintree-hosted-fields-focused {
	border-color: #0d6efd;
}

.payment-skeleton {
	display: grid;
	gap: var(--size-base);
	grid-template-columns: 1fr 1fr;

	&__item {
		height: var(--custom-control-height);
		border-radius: var(--size-sm);
		background: var(--gray-200);
		grid-column: span 2;
		position: relative;
		overflow: hidden;

		&.--half {
			grid-column: span 1;
		}

		&::after {
			will-change: transform;
			position: absolute;
			content: '';
			animation: shimmer 2s infinite;
			inset: 0;
			width: 100%;
			background: linear-gradient(
				to right,
				var(--gray-200) 4%,
				var(--gray-300) 25%,
				var(--gray-200) 36%
			);
		}
	}
}
</style>
